import { destroy, flow, getRoot, Instance, types } from 'mobx-state-tree';

import spotifyService from '../services/spotifyService';
import { IAlbumModel } from './models/AlbumModel';
import SearchModel from './models/SearchModel';
import { IRootStore } from './RootStore';

const SearchStore = types
    .model('SearchStore', {
        history: types.map(SearchModel),
        results: types.maybe(types.safeReference(SearchModel, { acceptsUndefined: false })),
    })
    .volatile(() => ({
        isSearching: false,
        searchTerm: '',
        selectedAlbumId: '',
    }))
    .views(self => ({
        get selectedAlbum(): IAlbumModel | undefined {
            const rootStore: IRootStore = getRoot(self);
            return rootStore.music.albums.get(self.selectedAlbumId);
        },
        get selectedAlbumTracks() {
            if (!this.selectedAlbum) {
                return [];
            }
            return Array.from(this.selectedAlbum.tracks.values());
        },
    }))
    .actions(self => {
        const rootStore: IRootStore = getRoot(self);
        const search = flow(function*(searchTerm: string) {
            const parsedSearchTerm = searchTerm.toLowerCase();
            self.isSearching = true;
            const isInHistory = self.history.get(parsedSearchTerm);
            if (!isInHistory) {
                // Getting results from API
                const response: SpotifyApi.SearchResponse = yield spotifyService.search(parsedSearchTerm, ['track', 'album']);
                console.log(response);
                self.history.set(parsedSearchTerm, { id: parsedSearchTerm });
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const historyRecord = self.history.get(parsedSearchTerm)!;
                if (response.tracks) {
                    response.tracks.items.forEach(track => {
                        const newTrack = rootStore.music.addTrack(track);
                        // self.results.push(newTrack);
                        historyRecord.addTrack(newTrack);
                    });
                    //
                    self.history.set(parsedSearchTerm, historyRecord);
                }
                if (response.albums) {
                    (response.albums.items as SpotifyApi.AlbumObjectFull[]).forEach(album => {
                        const newAlbum = rootStore.music.addAlbum({
                            id: album.id,
                            name: album.name,
                            artist: album.artists.map(artist => artist.name).join(', '),
                            image: album.images.length ? album.images[0].url : '',
                        });
                        historyRecord.addAlbum(newAlbum);
                    });
                }
                self.results = historyRecord;
                self.isSearching = false;
                return;
            }
            // Getting results from history
            self.results = isInHistory;
            self.isSearching = false;
        });

        const cleanResults = () => {
            if (self.results) {
                destroy(self.results);
            }
        };

        const setSearchTerm = (searchTerm: string) => {
            self.searchTerm = searchTerm;
            if (!searchTerm) {
                cleanResults();
            }
        };

        const selectAlbum = (albumId: string) => {
            self.selectedAlbumId = albumId;
            rootStore.music.albums.get(albumId)?.getTracks();
        };

        const deselectAlbum = () => {
            self.selectedAlbumId = '';
        };

        return { search, cleanResults, setSearchTerm, selectAlbum, deselectAlbum };
    });

export interface ISearchStore extends Instance<typeof SearchStore> {}

export default SearchStore;
